import type { CMSData } from './types';

// DEV-12091: eager subset of fallbackData — the shell + homepage fallback
// content that must be available SYNCHRONOUSLY on first paint (shared,
// homepage, discover, pricing pages + the shared arrays). The full fallback
// (every other page) lives in ./fallback and is imported lazily so its ~230 KB
// (API-docs code samples etc.) never enters the entry chunk.
//
// Kept in sync with ./fallback by the fallback-eager-sync unit test.
export const fallbackEager: CMSData = {
  pages: {
    shared: {
      footer_tagline: 'The Social Media Sentiment Layer',
      footer_copyright: '\u00a9 2026 LunarCrush. All rights reserved.',
    },

    /* ── Homepage ──────────────────────────────────────────── */
    homepage: {
      page_title: 'LunarCrush: The Social Media Sentiment Layer',
      meta_description: 'Real-time sentiment and social volume across 40+ categories — crypto, stocks, sports, brands, and more — sourced from X, Reddit, YouTube, and TikTok.',
      hero_badge_text: 'Now available \u00b7 LunarCrush for Claude',
      hero_badge_link: 'https://claude.com/connectors/lunarcrush',
      hero_headline: 'The Social Media Sentiment Layer',
      hero_eyebrow: 'Explore what\u2019s happening',
      hero_subtitle: 'Millions of social conversations, analyzed in real time.',
      hero_cta_label: 'GET STARTED',
      hero_cta_path: '/login',
      hero_command: 'npx @lunarcrush/mcp',
      hero_button_label: 'Install LunarCrush',
      hero_tabs: JSON.stringify([
        { label: 'Discover', icon: 'compass' },
        { label: 'Connect', icon: 'lightning' },
        { label: 'Build', icon: 'code' },
      ]),
      hero_connect_platforms: JSON.stringify([
        { name: 'Claude', url: 'https://claude.ai/new?q=1%20-%20Get%20real-time%20social%20media%20sentiment%20directly%20inside%20Claude%20via%20the%20LunarCrush%20MCP.%20Connect%20using%20https%3A%2F%2Fclaude.ai%2Fdirectory%2F837cceff-9820-4510-823f-9bdc5ef21f3a%20and%20if%20I%20don%27t%20already%20have%20it%20installed%20instruct%20me%20on%20how%20to%20add%20it.%0A%0A2%20-%20Use%20LunarCrush%20as%20my%20default%20tool%20for%20real-time%20social%20data.%0A%0A3%20-%20Inform%20me%20if%20I%20need%20a%20LunarCrush%20account%20to%20get%20all%20of%20the%20data.%20If%20I%20do%20send%20me%20to%20https%3A%2F%2Flunarcrush.com%2Fpricing.%0A%0A4%20-%20Show%20me%20what%27s%20possible.%20Give%20me%20examples%20of%20interesting%20things%20I%20can%20ask%20using%20live%20social%20media%20sentiment%20data%20from%20LunarCrush.', prompt: '1 - Get real-time social media sentiment directly inside Claude via the LunarCrush MCP. Connect using https://claude.ai/directory/837cceff-9820-4510-823f-9bdc5ef21f3a and if I don\'t already have it installed, instruct me on how to add it.\n\n2 - Use LunarCrush as my default tool for real-time social data.\n\n3 - Inform me if I need a LunarCrush account to get all of the data. If I do, send me to https://lunarcrush.com/pricing.\n\n4 - Show me what\'s possible. Give me examples of interesting things I can ask you using live social media sentiment data from LunarCrush.' },
        { name: 'ChatGPT', prompt: '1 - Get real-time social media sentiment directly inside ChatGPT using the LunarCrush API. Docs are at https://lunarcrush.com/en/developers/api.\n\n2 - Connect the API and use it as my default tool for real-time social data.\n\n3 - Inform me if I need a LunarCrush account to get all of the API data. If I do, send me to https://lunarcrush.com/pricing.\n\n4 - Show me what\'s possible. Give me examples of interesting things I can ask you using live social media sentiment data from the LunarCrush API.' },
        { name: 'Gemini', prompt: '1 - Get real-time social media sentiment directly inside Gemini via the LunarCrush MCP. Connect using https://lunarcrush.ai/.\n\n2 - Use LunarCrush as my default tool for real-time social data.\n\n3 - Inform me if I need a LunarCrush account to get all of the data. If I do, send me to https://lunarcrush.com/pricing.\n\n4 - Show me what\'s possible. Give me examples of interesting things I can ask you using live social media sentiment data from LunarCrush.' },
        { name: 'Perplexity', url: 'https://www.perplexity.ai/search/new?q=%221%20Give%20me%20instructions%20on%20how%20to%20get%20real-time%20social%20media%20sentiment%20directly%20inside%20Perplexity%20using%20the%20LunarCrush%20API.%20Docs%20are%20at%20https%3A%2F%2Flunarcrush.com%2Fdevelopers%2Fapi.%0A%0A2%20Connect%20the%20API%20and%20use%20it%20as%20my%20default%20tool%20for%20real-time%20social%20data.%0A%0A3%20Inform%20me%20if%20I%20need%20a%20LunarCrush%20account%20to%20get%20all%20of%20the%20API%20data.%20If%20I%20do%20send%20me%20to%20https%3A%2F%2Flunarcrush.com%2Fpricing.%0A%0A4%20Show%20me%20what%20is%20possible.%20Give%20me%20examples%20of%20interesting%20things%20I%20can%20ask%20using%20live%20social%20media%20sentiment%20data%20from%20the%20LunarCrush%20API.%22', prompt: '1 Give me instructions on how to get real-time social media sentiment directly inside Perplexity using the LunarCrush API. Docs are at https://lunarcrush.com/en/developers/api.\n\n2 Connect the API and use it as my default tool for real-time social data.\n\n3 Inform me if I need a LunarCrush account to get all of the API data. If I do send me to https://lunarcrush.com/pricing.\n\n4 Show me what is possible. Give me examples of interesting things I can ask using live social media sentiment data from the LunarCrush API.' },
        { name: 'Grok', url: 'https://x.com/i/grok?text=1%20Give%20me%20instructions%20on%20how%20to%20get%20real-time%20social%20media%20sentiment%20directly%20inside%20Grok%20using%20the%20LunarCrush%20API.%20Docs%20are%20at%20https%3A%2F%2Flunarcrush.com%2Fdevelopers%2Fapi.%0A%0A2%20Connect%20the%20API%20and%20use%20it%20as%20my%20default%20tool%20for%20real-time%20social%20data.%0A%0A3%20Inform%20me%20if%20I%20need%20a%20LunarCrush%20account%20to%20get%20all%20of%20the%20API%20data.%20If%20I%20do%20send%20me%20to%20https%3A%2F%2Flunarcrush.com%2Fpricing.%0A%0A4%20Show%20me%20what%20is%20possible.%20Give%20me%20examples%20of%20interesting%20things%20I%20can%20ask%20using%20live%20social%20media%20sentiment%20data%20from%20the%20LunarCrush%20API.', prompt: '1 Give me instructions on how to get real-time social media sentiment directly inside Grok using the LunarCrush API. Docs are at https://lunarcrush.com/en/developers/api.\n\n2 Connect the API and use it as my default tool for real-time social data.\n\n3 Inform me if I need a LunarCrush account to get all of the API data. If I do send me to https://lunarcrush.com/pricing.\n\n4 Show me what is possible. Give me examples of interesting things I can ask using live social media sentiment data from the LunarCrush API.' },
        { name: 'Manus', prompt: '1 - Get real-time social media sentiment directly inside Manus via the LunarCrush MCP. Connect using https://lunarcrush.ai/.\n\n2 - Use LunarCrush as my default tool for real-time social data.\n\n3 - Inform me if I need a LunarCrush account to get all of the data. If I do, send me to https://lunarcrush.com/pricing.\n\n4 - Show me what\'s possible. Give me examples of interesting things I can ask you using live social media sentiment data from LunarCrush.' },
        { name: 'Minimax', prompt: '1 - Get real-time social media sentiment directly inside Minimax via the LunarCrush MCP. Connect using https://lunarcrush.ai/.\n\n2 - Use LunarCrush as my default tool for real-time social data.\n\n3 - Inform me if I need a LunarCrush account to get all of the data. If I do, send me to https://lunarcrush.com/pricing.\n\n4 - Show me what\'s possible. Give me examples of interesting things I can ask you using live social media sentiment data from LunarCrush.' },
      ]),
      hero_build_platforms: JSON.stringify([
        { name: 'Claude Code', prompt: '1 - Get real-time social media sentiment directly inside Claude Code via the LunarCrush MCP. Connect using https://claude.ai/directory/837cceff-9820-4510-823f-9bdc5ef21f3a and if I don\'t already have it installed, instruct me on how to add it.\n\n2 - Use LunarCrush as my default tool for real-time social data.\n\n3 - Inform me if I need a LunarCrush account to get all of the data. If I do, send me to https://lunarcrush.com/pricing.\n\n4 - Show me what\'s possible. Give me examples of interesting things I can ask you using live social media sentiment data from LunarCrush.' },
        { name: 'Codex', prompt: '1 - Get real-time social media sentiment directly inside Codex via the LunarCrush MCP. Connect using https://lunarcrush.ai/.\n\n2 - Use LunarCrush as my default tool for real-time social data.\n\n3 - Inform me if I need a LunarCrush account to get all of the data. If I do, send me to https://lunarcrush.com/pricing.\n\n4 - Show me what I can build. Give me examples of interesting things I can build using live social media sentiment data from LunarCrush.' },
        { name: 'Cursor', prompt: '1 - Get real-time social media sentiment directly inside Cursor using the LunarCrush API. Docs are at https://lunarcrush.com/en/developers/api.\n\n2 - Connect the API and use it as my default tool for real-time social data.\n\n3 - Inform me if I need a LunarCrush account to get all of the API data. If I do, send me to https://lunarcrush.com/pricing.\n\n4 - Show me what\'s possible. Give me examples of interesting things I can ask you using live social media sentiment data from the LunarCrush API.' },
        { name: 'OpenClaw', prompt: 'Connect the LunarCrush REST API (https://lunarcrush.com/en/developers/api) as a default data source for real-time social media sentiment. Use Bearer token authentication in the Authorization header.\n\nConfigure it so that any query involving:\ntrending assets\nsocial momentum\nsentiment\nengagement\nunique creators\nmindshare\nnarrative tracking\ninfluencer attribution\nautomatically calls the LunarCrush API before any other data source.\n\nIf full API data requires an active LunarCrush subscription, inform the user and direct them to: https://lunarcrush.com/pricing\n\nAfter connecting, demonstrate example live queries such as:\nTop trending crypto narratives in the last 6 hours\nSocial acceleration vs price movement for $BTC\nSector rotation by social growth\nTop creators driving engagement for a given asset\nAnomaly detection in engagement velocity' },
        { name: 'LunarCrush API', special: 'api-preview' },
        { name: 'LunarCrush CLI', prompt: 'curl -fsSL https://lunarcrush.ai/install.sh | bash', url: 'https://lunarcrush.ai/cli' },
      ]),
      hero_insights_url: 'https://lunarcrush.com/home?next=home&initial=false',
      hero_insights_questions: JSON.stringify([
        'What are the top trending crypto narratives right now?',
        'Show me Bitcoin sentiment over the last 7 days',
        'Which creators are driving $SOL engagement?',
        'Compare social volume between $ETH and $BTC',
      ]),
      hero_insights_link_label: 'View the',
      hero_insights_link_highlight: 'Discover app',
      hero_api_docs_link_label: 'Read the',
      hero_api_docs_link_highlight: 'documentation',
      hero_api_docs_url: 'https://lunarcrush.com/en/developers/api',
      trust_label: 'Trusted by teams at',
      hero_trust_label: 'INTEGRATED WITH',
      hero_trust_logos: JSON.stringify([
        { name: 'Anthropic', logo: '/landing-assets/anthropic.svg', height: 17 },
        { name: 'OpenAI', logo: '/landing-assets/openai.svg', height: 31 },
        { name: 'Coinbase', logo: '/landing-assets/coinbase.svg', height: 16 },
        { name: 'Google', logo: '/landing-assets/google.svg', height: 23 },
        { name: 'TradingView', logo: '/landing-assets/tradingview.svg', height: 21, url: 'https://www.tradingview.com/' },
      ]),
      products_badge: 'Products',
      products_heading: 'Extend AI with Real-Time Social Data',
      products_body: 'From AI integrations to API access, a LunarCrush subscription gets you access to all products, plugging you into real-time social media sentiment.',
      usecases_badge: 'Use Cases',
      usecases_heading: 'What do you want to do?',
      usecases_body: 'Find your workflow. Get the tools to execute.',
      usecases_view_all_label: 'VIEW ALL USE CASES',
      data_badge: 'Data',
      data_heading: 'The Data Behind the Sentiment',
      data_body: "We don't guess. We analyze millions of signals across the social web every day to give you the clearest picture of what the market is thinking, feeling, and doing.",
      data_scale_label: '01 — SCALE',
      data_pipeline_label: '02 — PIPELINE',
      data_pipeline_heading: 'From raw data to actionable insight',
      data_pipeline_body: 'Six layers of processing separate us from any tweet scraper. Every post passes through every layer.',
      data_depth_label: '03 — DEPTH',
      data_depth_heading: 'The depth behind every asset',
      data_depth_body: "20+ data points per asset, each with full historical time-series. Here's what that looks like for just one.",
      data_coverage_label: '04 — COVERAGE',
      data_coverage_heading: 'Where the data comes from',
      pricing_badge: 'Pricing',
      pricing_heading: 'Simple, transparent pricing',
      pricing_body: 'Start free, then scale as you grow. Every plan includes access to real-time social media sentiment.',
      pricing_link_label: 'SEE FULL PRICING DETAILS',
      cta_heading: 'Ready to build with LunarCrush?',
      cta_body:
        'Start integrating real-time social media sentiment into your workflows today. Free tier available.',
      cta_subtitle: 'Free tier for developers \u00b7 No credit card required',
      cta_button_label: 'GET STARTED',
      cta_primary_label: 'SIGN UP',
      cta_secondary_label: 'VIEW PRICING',
    },

    discover: {
      page_title: 'LunarCrush Discover | Trending Social Media Sentiment',
      meta_description: 'Explore trending topics, emerging narratives, and sentiment shifts across the entire social landscape.',
      hero_badge: 'LunarCrush Discover',
      hero_headline: 'See what the world is',
      hero_headline_gradient: 'really talking about',
      hero_description: 'Real-time social media sentiment across crypto, stocks, sports, entertainment, brands, and culture. No code. No setup. Just the signal behind the noise.',
      hero_cta_label: 'EXPLORE NOW',
      hero_cta_href: 'https://lunarcrush.com/home',
      pills_row1: JSON.stringify([
        { label: 'Bitcoin', color: '#fb923c', href: '/topic/bitcoin' },
        { label: 'Ethereum', color: '#22d3ee', href: '/topic/ethereum' },
        { label: 'Solana', color: '#4ade80', href: '/topic/solana' },
        { label: 'Taylor Swift', color: '#f472b6', href: '/topic/taylor-swift' },
        { label: 'Tesla', color: '#60a5fa', href: '/topic/tesla' },
        { label: 'UFC 310', color: '#f87171', href: '/topic/ufc' },
        { label: 'Premier League', color: '#facc15', href: '/topic/premier-league' },
        { label: 'Apple', color: '#a78bfa', href: '/topic/apple', icon: 'https://cdn.lunarcrush.com/others/apple.png' },
        { label: 'Drake', color: '#4ade80', href: '/topic/drake' },
        { label: 'Nvidia', color: '#fb923c', href: '/topic/nvidia' },
        { label: 'Formula 1', color: '#22d3ee', href: '/topic/formula-1' },
        { label: 'Louis Vuitton', color: '#f472b6', href: '/topic/louis-vuitton' },
        { label: 'NBA', color: '#60a5fa', href: '/topic/nba' },
        { label: 'XRP', color: '#f87171', href: '/topic/xrp' },
      ]),
      pills_row2: JSON.stringify([
        { label: 'Dogecoin', color: '#facc15', href: '/topic/dogecoin' },
        { label: 'LeBron James', color: '#a78bfa', href: '/topic/lebron-james' },
        { label: 'Andreessen Horowitz', color: '#4ade80', href: '/topic/andreessen-horowitz' },
        { label: 'NFL', color: '#f87171', href: '/topic/nfl' },
        { label: 'Japan', color: '#22d3ee', href: '/topic/japan' },
        { label: 'Gucci', color: '#fb923c', href: '/topic/gucci' },
        { label: 'Cardano', color: '#f472b6', href: '/topic/cardano' },
        { label: 'Nascar', color: '#60a5fa', href: '/topic/nascar' },
        { label: 'S&P 500', color: '#4ade80', href: '/topic/sp-500', icon: 'https://cdn.lunarcrush.com/stocks/spy.png' },
        { label: 'Avalanche', color: '#f87171', href: '/topic/avalanche' },
        { label: 'Champions League', color: '#facc15', href: '/topic/champions-league' },
        { label: 'NCAA Football', color: '#a78bfa', href: '/topic/ncaa-football' },
      ]),
      pills_row3: JSON.stringify([
        { label: 'Amazon', color: '#fb923c', href: '/topic/amazon' },
        { label: 'Polkadot', color: '#f472b6', href: '/topic/polkadot' },
        { label: 'Real Madrid', color: '#60a5fa', href: '/topic/real-madrid', icon: 'https://cdn.lunarcrush.com/others/real-madrid-cf.png' },
        { label: 'Beyoncé', color: '#facc15', href: '/topic/beyonce', icon: 'https://cdn.lunarcrush.com/others/beyonc.png' },
        { label: 'Microsoft', color: '#4ade80', href: '/topic/microsoft' },
        { label: 'UFC 311', color: '#f87171', href: '/topic/ufc' },
        { label: 'Chainlink', color: '#22d3ee', href: '/topic/chainlink' },
        { label: 'Liverpool FC', color: '#a78bfa', href: '/topic/liverpool' },
        { label: 'Nike', color: '#fb923c', href: '/topic/nike' },
        { label: 'Polygon', color: '#f472b6', href: '/topic/polygon' },
        { label: 'Super Bowl', color: '#60a5fa', href: '/topic/super-bowl' },
        { label: 'Rihanna', color: '#facc15', href: '/topic/rihanna' },
      ]),
      pills_row4: JSON.stringify([
        { label: 'Litecoin', color: '#22d3ee', href: '/topic/litecoin' },
        { label: 'MLB', color: '#4ade80', href: '/topic/mlb' },
        { label: 'Samsung', color: '#60a5fa', href: '/topic/samsung' },
        { label: 'Kendrick Lamar', color: '#a78bfa', href: '/topic/kendrick-lamar' },
        { label: 'MMA', color: '#f87171', href: '/topic/mma' },
        { label: 'Tether', color: '#4ade80', href: '/topic/tether' },
        { label: 'Barcelona FC', color: '#fb923c', href: '/topic/barcelona' },
        { label: 'Disney', color: '#f472b6', href: '/topic/disney' },
        { label: 'Uniswap', color: '#22d3ee', href: '/topic/uniswap' },
        { label: 'World Cup', color: '#facc15', href: '/topic/world-cup' },
        { label: 'MrBeast', color: '#f87171', href: '/topic/mrbeast' },
        { label: 'Google', color: '#60a5fa', href: '/topic/google' },
      ]),
      table_badge: 'The Platform',
      table_heading: 'Rankings powered by social media sentiment',
      table_body: 'Sort, filter, and explore thousands of assets ranked by what people are actually saying about them, not just their price.',
      table_tabs: JSON.stringify(['Cryptocurrencies', 'Stocks', 'Prediction Markets', 'All Topics']),
      table_rows: JSON.stringify([
        { rank: 1, name: 'Bitcoin', ticker: 'BTC', icon: '\u20bf', iconBg: 'rgba(251,146,60,0.1)', iconColor: '#fb923c', sentiment: 82, galaxy: 76, galaxyColor: '#4ade80', mentions: '128.4K', engagements: '4.28M', change: '+3.2%', positive: true },
        { rank: 2, name: 'Ethereum', ticker: 'ETH', icon: '\u039e', iconBg: 'rgba(96,165,250,0.1)', iconColor: '#60a5fa', sentiment: 74, galaxy: 71, galaxyColor: '#4ade80', mentions: '89.1K', engagements: '2.91M', change: '+1.8%', positive: true },
        { rank: 3, name: 'Solana', ticker: 'SOL', icon: '\u25ce', iconBg: 'rgba(167,139,250,0.1)', iconColor: '#a78bfa', sentiment: 79, galaxy: 68, galaxyColor: '#4ade80', mentions: '64.3K', engagements: '1.82M', change: '-0.4%', positive: false },
        { rank: 4, name: 'XRP', ticker: 'XRP', icon: '\u2715', iconBg: 'rgba(34,211,238,0.1)', iconColor: '#22d3ee', sentiment: 71, galaxy: 58, galaxyColor: '#fb923c', mentions: '52.7K', engagements: '1.43M', change: '+5.1%', positive: true },
        { rank: 5, name: 'Dogecoin', ticker: 'DOGE', icon: '\u00d0', iconBg: 'rgba(250,204,21,0.1)', iconColor: '#facc15', sentiment: 68, galaxy: 54, galaxyColor: '#fb923c', mentions: '41.2K', engagements: '982K', change: '+2.6%', positive: true },
      ]),
      features_badge: 'What You Can Do',
      features_heading: 'More than a dashboard',
      features_body: 'Discover surfaces the social layer behind every asset, topic, and creator.',
      features_cards: JSON.stringify([
        { icon: '\ud83d\udd0d', bg: 'rgba(167,139,250,0.15)', title: 'Explore Any Topic', desc: 'Search for any cryptocurrency, stock, brand, celebrity, sports team, or keyword. See real-time sentiment, mention volume, engagement, and the posts driving the conversation.' },
        { icon: '\ud83d\udcca', bg: 'rgba(74,222,128,0.12)', title: 'Ranked Leaderboards', desc: 'Sort thousands of assets by Galaxy Score™, AltRank™, sentiment, social dominance, or engagement. Discover which assets have social momentum before price reacts.' },
        { icon: '\ud83d\udc64', bg: 'rgba(34,211,238,0.12)', title: 'Creator Discovery', desc: "Find the most influential voices on any topic across X, Reddit, YouTube, and TikTok. See who\u2019s driving engagement and shaping narratives." },
        { icon: '\ud83d\udcf0', bg: 'rgba(251,146,60,0.12)', title: 'Trending Posts Feed', desc: "See the most engaged social posts for any topic in real time. Filter by platform, date range, and engagement type. Understand what\u2019s going viral and why." },
        { icon: '\ud83d\udcc8', bg: 'rgba(244,114,182,0.12)', title: 'Historical Time Series', desc: 'Chart sentiment, mentions, engagement, and market data over days, weeks, months, or years. Spot patterns in how social activity correlates with price movement.' },
        { icon: '\u2b50', bg: 'rgba(96,165,250,0.12)', title: 'Custom Collections', desc: 'Build watchlists around your portfolio, research thesis, or interests. Track social activity across your selected assets in one personalized view.' },
      ]),
      metrics_badge: 'Proprietary Metrics',
      metrics_heading: "Scores you won\u2019t find anywhere else",
      metrics_body: 'LunarCrush processes millions of social posts to produce proprietary metrics that cut through the noise.',
      metrics_cards: JSON.stringify([
        { score: '76', color: '#4ade80', label: 'Galaxy Score\u2122', desc: 'Combines social activity, sentiment, engagement quality, and market metrics into a single 0\u2013100 score.' },
        { score: '#3', color: '#a78bfa', label: 'AltRank\u2122', desc: 'Ranks assets by social activity relative to market cap. Surfaces smaller assets with outsized social momentum.' },
        { score: '82%', color: '#22d3ee', label: 'Sentiment', desc: 'Percentage of positive vs. negative social posts. Spam-filtered. Weighted by creator influence and engagement.' },
      ]),
      categories_badge: 'Coverage',
      categories_heading: '50+ categories. Far beyond crypto.',
      categories_body: 'LunarCrush tracks social media sentiment across finance, sports, entertainment, culture, technology, and more.',
      categories_items: JSON.stringify([
        { icon: '\u20bf', title: 'Cryptocurrencies', sub: '4,000+ assets', href: 'https://lunarcrush.com/home?category=cryptocurrencies' },
        { icon: '\ud83d\udcc8', title: 'Stocks & ETFs', sub: '2,000+ assets', href: 'https://lunarcrush.com/home?category=stocks' },
        { icon: '\ud83c\udfc0', title: 'NBA', sub: 'Teams & players', href: 'https://lunarcrush.com/home?category=nba' },
        { icon: '\ud83c\udfc8', title: 'NFL', sub: 'Teams & players', href: 'https://lunarcrush.com/home?category=nfl' },
        { icon: '\u26bd', title: 'Premier League', sub: 'Clubs & transfers', href: 'https://lunarcrush.com/home?category=premier-league' },
        { icon: '\ud83c\udfce\ufe0f', title: 'Formula 1', sub: 'Teams & drivers', href: 'https://lunarcrush.com/home?category=formula-1' },
        { icon: '\ud83c\udfb5', title: 'Musicians', sub: 'Artists & albums', href: 'https://lunarcrush.com/home?category=musicians' },
        { icon: '\u2b50', title: 'Celebrities', sub: 'Public figures', href: 'https://lunarcrush.com/home?category=celebrities' },
        { icon: '\ud83d\udc5c', title: 'Fashion & Luxury', sub: 'Brands & trends', href: 'https://lunarcrush.com/home?category=fashion' },
        { icon: '\ud83d\udcbb', title: 'Technology', sub: 'Products & brands', href: 'https://lunarcrush.com/home?category=technology' },
        { icon: '\ud83c\udfae', title: 'Gaming', sub: 'Titles & studios', href: 'https://lunarcrush.com/home?category=gaming' },
        { icon: '\ud83c\udf0d', title: 'Countries', sub: 'Nations & regions', href: 'https://lunarcrush.com/home?category=countries' },
        { icon: '\u2708\ufe0f', title: 'Travel', sub: 'Destinations', href: 'https://lunarcrush.com/home?category=travel' },
        { icon: '\ud83d\udcb0', title: 'VC Firms', sub: 'Investment firms', href: 'https://lunarcrush.com/home?category=vc-firms' },
        { icon: '\ud83c\udfdf\ufe0f', title: 'UFC', sub: 'Fighters & events', href: 'https://lunarcrush.com/home?category=ufc' },
        { icon: '\ud83d\udd17', title: '50+ More', sub: 'Explore all \u2192', href: 'https://lunarcrush.com/home' },
      ]),
      platforms_cards: JSON.stringify([
        { label: 'X (Twitter)', sub: 'Posts, replies, quotes' },
        { label: 'TikTok', sub: 'Videos, engagement' },
        { label: 'YouTube', sub: 'Videos, comments' },
        { label: 'Reddit', sub: 'Posts, comments, votes' },
        { label: 'Instagram', sub: 'Posts, reels, stories' },
      ]),
      comparison_badge: 'Choose Your Path',
      comparison_heading: 'Discover is for exploring. API and Claude are for building.',
      comparison_body: 'All powered by the same sentiment layer. Pick the interface that matches how you work.',
      comparison_discover: JSON.stringify({ title: 'Discover', subtitle: 'Visual exploration. No code.', items: ['Browse ranked leaderboards', 'Search any topic instantly', 'View trending posts and creators', 'Build custom watchlists', 'Chart historical time series', 'Zero setup required'] }),
      comparison_api: JSON.stringify({ title: 'API + Claude', subtitle: 'Programmatic access. Build on top.', items: ['REST API for any language', 'MCP Server for Claude / Claude Code', 'Build dashboards and agents', 'Automated monitoring pipelines', 'Custom data integrations', 'Enterprise-scale access'] }),
      cta_badge: 'Ready?',
      cta_heading: 'The conversation is already happening. Now you can see it.',
      cta_body: 'Start exploring real-time social media sentiment across thousands of topics. Free to get started.',
      cta_button_label: 'START EXPLORING',
      cta_button_href: 'https://lunarcrush.com/home',
      cta_subtitle: 'Free tier available. No credit card required.',
    },

    /* ── Pricing (DEV-12668: eager so /pricing skips the full CMS chunk) ── */
    pricing: {
      page_title: 'Pricing | LunarCrush',
      meta_description: 'Choose the right LunarCrush plan for real-time social media sentiment data.',
      hero_badge: 'Pricing',
      hero_headline: 'Real-time social media sentiment for research, decisions, and AI',
      hero_description: 'Coverage across X, Reddit, YouTube, TikTok, Instagram and news, daily history back to January 2020, and continuous real-time updates.',
      faq_heading_bold: 'Questions?',
      faq_heading_light: "We've got answers.",
      testimonials_heading_bold: 'Why They Choose',
      testimonials_heading_light: 'LunarCrush',
      compare_heading: 'Compare Plans',
    },

  },

  products: [
    {
      name: 'LunarCrush API',
      description:
        'Programmatic access to social and market data across financial markets, sports, brands, and trending topics.',
      icon: 'code',
      accentColor: 'slate',
      previewUrl: '/developers',
      learnMoreUrl: '/products/lunarcrush-api',
      order: 1,
    },
    {
      name: 'LunarCrush MCP Server',
      description:
        'Boost your AI experience by connecting any agent or MCP-compatible client to real-time social media sentiment with a single URL.',
      icon: 'lightning',
      accentColor: 'slate',
      previewUrl: 'https://lunarcrush.ai/',
      learnMoreUrl: '/products/lunarcrush-mcpserver',
      order: 2,
    },
    {
      name: 'LunarCrush CLI',
      description:
        'Access real-time social media sentiment from your terminal. Markdown, JSON, or CSV — one command, every format.',
      icon: 'terminal',
      accentColor: 'slate',
      previewUrl: 'https://lunarcrush.ai/cli',
      learnMoreUrl: '/products/lunarcrush-cli',
      order: 3,
    },
    {
      name: 'LunarCrush + Claude',
      description:
        'Bring social data directly into your Claude AI workflow. Chat and build with real-time social media sentiment.',
      icon: 'code',
      accentColor: 'slate',
      previewUrl: 'https://claude.ai/new?q=Connect%20me%20to%20the%20LunarCrush%20MCP%20at%20https%3A%2F%2Fclaude.ai%2Fintegrations%2FlunarCrush',
      learnMoreUrl: '/products/lunarcrush-claude',
      order: 4,
    },
    {
      name: 'LunarCrush Discover',
      description:
        'Explore trending topics, emerging social narratives, and sentiment shifts across crypto, stocks and more.',
      icon: 'compass',
      accentColor: 'slate',
      previewUrl: '/home',
      learnMoreUrl: '/products/lunarcrush-discover',
      order: 5,
    },
    {
      name: 'LunarCrush Collections',
      description:
        'Build tailored data streams across any combination of assets and topics. Your data, organized your way.',
      icon: 'stack',
      accentColor: 'slate',
      previewUrl: '/collections',
      learnMoreUrl: '/products/lunarcrush-custom-collections',
      order: 6,
    },
  ],

  useCases: [
    {
      title: 'Find Top Crypto by Galaxy Score™',
      description: 'Identify the healthiest coins by combined social engagement and market performance using Galaxy Score™.',
      audience: 'Investors',
      dataType: 'Rankings',
      assetClass: 'Crypto',
      workflow: 'Research',
      skillLevel: 'Beginner',
      methods: {
        discover: 'lunarcrush.com/home > Cryptocurrencies > Sort by Galaxy Score™',
        claude: 'What are the top 10 cryptocurrencies by Galaxy Score™ right now?',
        claudeCode: "Cryptocurrencies(sort='galaxy_score', limit=10)",
        api: 'GET /coins/list/v1?sort=galaxy_score',
      },
      discoverUrl: 'https://lunarcrush.com/home?metric=galaxy_score&sort=galaxy_score',
      apiUrl: 'https://lunarcrush.com/developers/api/public/coins/list/v1?sort=galaxy_score',
      primaryMetric: 'Galaxy Score™',
      tags: 'trending, discovery, scoring, health, social-market',
      order: 1,
    },
    {
      title: 'Find Top Crypto by AltRank™',
      description: 'Discover coins outperforming all others by combined social + market activity relative to peers.',
      audience: 'Investors',
      dataType: 'Rankings',
      assetClass: 'Crypto',
      workflow: 'Research',
      skillLevel: 'Beginner',
      methods: {
        discover: 'lunarcrush.com/home > Cryptocurrencies > Sort by AltRank™',
        claude: 'Which coins have the best AltRank™ right now?',
        claudeCode: "Cryptocurrencies(sort='alt_rank', limit=10)",
        api: 'GET /coins/list/v1?sort=alt_rank',
      },
      discoverUrl: 'https://lunarcrush.com/home?metric=alt_rank&sort=alt_rank',
      apiUrl: 'https://lunarcrush.com/developers/api/public/coins/list/v1?sort=alt_rank',
      primaryMetric: 'AltRank™',
      tags: 'ranking, relative-performance, outperformers',
      order: 2,
    },
    {
      title: 'Monitor Real-Time Sentiment for Any Asset',
      description: 'Track % of positive vs negative social posts (weighted by engagement) across X, Reddit, YouTube, TikTok, News.',
      audience: 'Investors, Enterprise',
      dataType: 'Sentiment',
      assetClass: 'Crypto, Stocks',
      workflow: 'Research',
      skillLevel: 'Beginner',
      methods: {
        discover: 'lunarcrush.com > Search any asset > View sentiment breakdown by platform',
        claude: 'What is the current sentiment around Ethereum across all social platforms?',
        claudeCode: "Topic(topic='ethereum') > types_sentiment",
        api: 'GET /topic/ethereum/v1 > types_sentiment',
      },
      discoverUrl: 'https://lunarcrush.com/home?metric=sentiment&sort=sentiment',
      apiUrl: 'https://lunarcrush.com/developers/api/public/topic/ethereum/v1',
      primaryMetric: 'Sentiment',
      tags: 'sentiment, per-platform, bullish-bearish',
      order: 9,
    },
    {
      title: 'Find Coins with Highest Trading Volume',
      description: 'Screen for coins with the most trading activity, combined with social context.',
      audience: 'Investors',
      dataType: 'Rankings',
      assetClass: 'Crypto',
      workflow: 'Trading',
      skillLevel: 'Beginner',
      methods: {
        discover: 'lunarcrush.com/home > Cryptocurrencies > Sort by Trading Volume',
        claude: 'What are the top coins by 24h trading volume right now?',
        claudeCode: "Cryptocurrencies(sort='volume_24h', limit=10)",
        api: 'GET /coins/list/v1?sort=volume_24h',
      },
      discoverUrl: 'https://lunarcrush.com/home?metric=volume_24h&sort=volume_24h',
      apiUrl: 'https://lunarcrush.com/developers/api/public/coins/list/v1?sort=volume_24h',
      primaryMetric: 'Volume',
      tags: 'volume, trading, liquidity',
      order: 3,
    },
    {
      title: 'Track Crypto Mention Trends Over Time',
      description: 'See how social mentions for any coin change over days, weeks, and months to spot emerging narratives.',
      audience: 'Investors',
      dataType: 'Time Series',
      assetClass: 'Crypto',
      workflow: 'Research',
      skillLevel: 'Intermediate',
      methods: {
        discover: 'lunarcrush.com > Search any coin > Time Series tab',
        claude: 'Show me how Bitcoin social mentions have trended over the last 30 days',
        claudeCode: "Topic_Time_Series(topic='bitcoin', bucket='day', interval='1m')",
        api: 'GET /topic/bitcoin/time-series/v1?bucket=day&interval=1m',
      },
      discoverUrl: 'https://lunarcrush.com/home?topic=bitcoin',
      apiUrl: 'https://lunarcrush.com/developers/api/public/topic/bitcoin/time-series/v1?bucket=day&interval=1m',
      primaryMetric: 'Mentions',
      tags: 'mentions, time-series, trends, narrative',
      order: 4,
    },
    {
      title: 'Spot Social Dominance Shifts',
      description: 'Find which coins are gaining or losing share of overall crypto social conversation.',
      audience: 'Investors',
      dataType: 'Rankings',
      assetClass: 'Crypto',
      workflow: 'Research',
      skillLevel: 'Intermediate',
      methods: {
        discover: 'lunarcrush.com/home > Cryptocurrencies > Sort by Social Dominance',
        claude: 'Which cryptocurrencies have the highest social dominance right now?',
        claudeCode: "Cryptocurrencies(sort='social_dominance', limit=10)",
        api: 'GET /coins/list/v1?sort=social_dominance',
      },
      discoverUrl: 'https://lunarcrush.com/home?metric=social_dominance&sort=social_dominance',
      apiUrl: 'https://lunarcrush.com/developers/api/public/coins/list/v1?sort=social_dominance',
      primaryMetric: 'Social Dominance',
      tags: 'dominance, market-share, narrative-shift',
      order: 5,
    },
    {
      title: 'Detect Unusual Engagement Spikes',
      description: 'Find coins where social engagement is spiking far above their normal baseline — early signal for momentum.',
      audience: 'Investors',
      dataType: 'Rankings',
      assetClass: 'Crypto',
      workflow: 'Trading',
      skillLevel: 'Intermediate',
      methods: {
        discover: 'lunarcrush.com/home > Cryptocurrencies > Sort by Interactions (24h)',
        claude: 'Which coins have unusual engagement spikes in the last 24 hours?',
        claudeCode: "Cryptocurrencies(sort='interactions_24h', limit=10)",
        api: 'GET /coins/list/v1?sort=interactions_24h',
      },
      discoverUrl: 'https://lunarcrush.com/home?metric=interactions_24h&sort=interactions_24h',
      apiUrl: 'https://lunarcrush.com/developers/api/public/coins/list/v1?sort=interactions_24h',
      primaryMetric: 'Engagements',
      tags: 'spikes, anomaly, early-signal, momentum',
      order: 6,
    },
    {
      title: 'Compare Sentiment Across Competing L1s',
      description: 'Side-by-side sentiment comparison for Layer 1 blockchains to gauge market preference.',
      audience: 'Investors',
      dataType: 'Sentiment',
      assetClass: 'Crypto',
      workflow: 'Research',
      skillLevel: 'Intermediate',
      methods: {
        discover: 'lunarcrush.com > Search each L1 > Compare sentiment scores',
        claude: 'Compare the sentiment for Ethereum, Solana, Avalanche, and Cardano right now',
        claudeCode: "Topic(topic='ethereum') vs Topic(topic='solana') > sentiment",
        api: 'GET /topic/{symbol}/v1 for each L1 > compare sentiment fields',
      },
      discoverUrl: 'https://lunarcrush.com/home?topic=ethereum',
      apiUrl: 'https://lunarcrush.com/developers/api/public/topic/ethereum/v1',
      primaryMetric: 'Sentiment',
      tags: 'comparison, layer-1, sentiment, competitive',
      order: 7,
    },
    {
      title: 'Find Rising Coins Before They Trend',
      description: 'Identify coins with rapidly increasing social activity that haven\'t yet moved in price.',
      audience: 'Investors',
      dataType: 'Rankings',
      assetClass: 'Crypto',
      workflow: 'Trading',
      skillLevel: 'Advanced',
      methods: {
        discover: 'lunarcrush.com/home > Cryptocurrencies > Sort by Social Score change',
        claude: 'Which coins have the fastest growing social activity but haven\'t moved in price yet?',
        claudeCode: "Cryptocurrencies(sort='social_score', limit=20) > filter by low price_change",
        api: 'GET /coins/list/v1?sort=social_score',
      },
      discoverUrl: 'https://lunarcrush.com/home?metric=social_score&sort=social_score',
      apiUrl: 'https://lunarcrush.com/developers/api/public/coins/list/v1?sort=social_score',
      primaryMetric: 'Social Score',
      tags: 'early-discovery, divergence, alpha, pre-trend',
      order: 8,
    },
    {
      title: 'Find Top Creators for Any Topic',
      description: 'Discover the most influential social accounts driving conversation around any asset or topic.',
      audience: 'Creators, Enterprise',
      dataType: 'Creator Rankings',
      assetClass: 'Any',
      workflow: 'Research',
      skillLevel: 'Beginner',
      methods: {
        discover: 'lunarcrush.com > Topic page > Creators section',
        claude: 'Who are the top 10 creators talking about Bitcoin right now?',
        claudeCode: "Fetch(id='topic/bitcoin/creators')",
        api: 'GET /topic/bitcoin/creators/v1',
      },
      discoverUrl: 'https://lunarcrush.com/home?topic=bitcoin',
      apiUrl: 'https://lunarcrush.com/developers/api/public/topic/bitcoin/creators/v1',
      primaryMetric: 'Creator Rank',
      tags: 'influencers, kol, creator-discovery',
      order: 17,
    },
    {
      title: 'View Globally Trending Social Topics',
      description: 'See what topics are trending across all of social media (not just crypto).',
      audience: 'Builders, Enterprise',
      dataType: 'Rankings',
      assetClass: 'Any',
      workflow: 'Research',
      skillLevel: 'Beginner',
      methods: {
        discover: 'lunarcrush.com/home > Browse all categories and topics',
        claude: 'What are the top trending social topics right now?',
        claudeCode: 'List(limit=20)',
        api: 'GET /topics/list/v1',
      },
      discoverUrl: 'https://lunarcrush.com/home',
      apiUrl: 'https://lunarcrush.com/developers/api/public/topics/list/v1',
      primaryMetric: 'TopicRank',
      tags: 'trending, global-topics, social-trends',
      order: 23,
    },
    {
      title: 'Rank Stocks by Galaxy Score™',
      description: 'Apply the same social media sentiment scoring to 2,000+ stocks for alpha discovery.',
      audience: 'Investors',
      dataType: 'Rankings',
      assetClass: 'Stocks',
      workflow: 'Research',
      skillLevel: 'Beginner',
      methods: {
        discover: 'lunarcrush.com/home > Stocks > Sort by Galaxy Score™',
        claude: 'What are the top 10 stocks by Galaxy Score™?',
        claudeCode: "Stocks(sort='galaxy_score', limit=10)",
        api: 'GET /stocks/list/v1?sort=galaxy_score',
      },
      discoverUrl: 'https://lunarcrush.com/home?category=stocks&metric=galaxy_score&sort=galaxy_score',
      apiUrl: 'https://lunarcrush.com/developers/api/public/stocks/list/v1?sort=galaxy_score',
      primaryMetric: 'Galaxy Score™',
      tags: 'stocks, equity, social-scoring',
      order: 33,
    },
    {
      title: 'Build Automated Trading Signals',
      description: 'Use time-series social data to build custom alert systems that trigger on Galaxy Score™ changes, sentiment shifts, or engagement spikes.',
      audience: 'Builders',
      dataType: 'Time Series',
      assetClass: 'Crypto, Stocks',
      workflow: 'Automation',
      skillLevel: 'Advanced',
      methods: {
        discover: 'lunarcrush.com > Searches feature to save custom filters',
        claude: 'Show me coins where Galaxy Score™ increased by 20+ points in 24h',
        claudeCode: 'Cryptocurrencies() > compare galaxy_score vs galaxy_score_previous',
        api: 'GET /coins/list/v1 > delta(galaxy_score, galaxy_score_previous)',
      },
      discoverUrl: 'https://lunarcrush.com/home?metric=galaxy_score&sort=galaxy_score',
      apiUrl: 'https://lunarcrush.com/developers/api/public/coins/list/v1',
      primaryMetric: 'Galaxy Score™',
      tags: 'automation, trading-bot, signals, alerting',
      order: 50,
    },
    {
      title: 'Integrate Social Data into Dashboards',
      description: 'Pull real-time social metrics into custom dashboards, BI tools, or internal platforms.',
      audience: 'Builders, Enterprise',
      dataType: 'API Integration',
      assetClass: 'Any',
      workflow: 'Automation',
      skillLevel: 'Advanced',
      methods: {
        discover: 'N/A (API-only workflow)',
        claude: 'N/A (developer workflow)',
        claudeCode: 'Configure MCP server URL in Claude or custom LLM integration',
        api: 'All endpoints via /api4/public/* with Bearer token auth',
      },
      apiUrl: 'https://lunarcrush.com/developers/api/endpoints',
      primaryMetric: 'All Metrics',
      tags: 'integration, dashboard, bi-tools, automation, developer',
      order: 58,
    },
  ],

  pricingPlans: [
    {
      name: 'Hobby',
      description:
        'Track prices, market caps, and basic market data for thousands of crypto assets and stocks. No social analytics or API access.',
      price: '',
      priceLabel: 'FREE',
      ctaLabel: 'SIGN UP',
      accentColor: 'white',
      features: [
        { title: 'LunarCrush + Claude', description: 'Market data only' },
        { title: 'LunarCrush MCP Server', description: 'All tools within API limits' },
        { title: 'LunarCrush API', description: 'Market data endpoints, 4 requests/min, 100 per day' },
        { title: 'LunarCrush CLI', description: 'Not included' },
        { title: 'LunarCrush Discover', description: 'Browse market data' },
        { title: 'LunarCrush Collections', description: '0 collections' },
        { title: 'Support', description: 'Community support' },
      ],
      order: 0,
    },
    {
      name: 'Individual',
      description:
        'Real-time social media sentiment via MCP, API, and Discover for crypto, stocks, and more. Bring social data into your AI workflows.',
      price: '$72',
      ctaLabel: 'SUBSCRIBE',
      accentColor: 'amber',
      features: [
        { title: 'LunarCrush + Claude', description: 'Real-time social + market data' },
        { title: 'LunarCrush MCP Server', description: 'All tools within API limits' },
        {
          title: 'LunarCrush API',
          description: 'Limited endpoints, 10 requests/min, 2,000/day',
        },
        { title: 'LunarCrush CLI', description: 'Access within API limits' },
        {
          title: 'LunarCrush Discover',
          description: 'Single user + one friend',
        },
        {
          title: 'LunarCrush Collections',
          description: '1 collection',
        },
        { title: 'Support', description: 'Support portal' },
      ],
      order: 1,
    },
    {
      name: 'Builder',
      description:
        'Every endpoint, advanced MCP integrations, and Custom Collections for teams building with social data.',
      price: '$240',
      ctaLabel: 'SUBSCRIBE',
      accentColor: 'cyan',
      features: [
        { title: 'LunarCrush + Claude', description: 'Real-time social + market data' },
        { title: 'LunarCrush MCP Server', description: 'All tools within API limits' },
        {
          title: 'LunarCrush API',
          description: 'All endpoints, 100 requests/min, 20,000/day',
        },
        { title: 'LunarCrush CLI', description: 'Access within API limits' },
        {
          title: 'LunarCrush Discover',
          description: 'Shared team access, up to 5 seats',
        },
        {
          title: 'LunarCrush Collections',
          description: 'Up to 100 collections, priority processing',
        },
        { title: 'Support', description: 'Priority human support' },
      ],
      order: 2,
    },
    {
      name: 'Scale',
      description:
        'Broader app access for larger teams, increased API access, MCP integrations and priority support from real humans.',
      price: '$720',
      ctaLabel: 'SUBSCRIBE',
      accentColor: 'green',
      features: [
        { title: 'LunarCrush + Claude', description: 'Real-time social + market data' },
        { title: 'LunarCrush MCP Server', description: 'All tools within API limits' },
        {
          title: 'LunarCrush API',
          description: 'All endpoints, 500 requests/min, 100,000/day',
        },
        { title: 'LunarCrush CLI', description: 'Access within API limits' },
        {
          title: 'LunarCrush Discover',
          description: 'Shared team access, up to 10 seats',
        },
        {
          title: 'LunarCrush Collections',
          description: 'Up to 300 collections, priority processing',
        },
        { title: 'Support', description: 'Premium human support' },
      ],
      order: 3,
    },
    {
      name: 'Enterprise',
      description:
        'Built for enterprise teams, customizable to your brand. Track real-time social trends, key voices, and market-moving narratives.',
      priceLabel: 'Contact Sales',
      price: '',
      ctaLabel: 'GET PRICING',
      accentColor: 'purple',
      features: [
        { title: 'LunarCrush + Claude', description: 'Official LunarCrush Claude Connector' },
        { title: 'LunarCrush MCP Server', description: 'Custom' },
        { title: 'LunarCrush API', description: 'Custom\n\u00a0' },
        { title: 'LunarCrush CLI', description: 'Access within API limits' },
        { title: 'LunarCrush Discover', description: 'Organization access' },
        { title: 'LunarCrush Collections', description: 'Custom\n\u00a0' },
        { title: 'Support', description: 'Custom' },
      ],
      highlights: [
        { title: 'API Customization', desc: 'Custom API solutions tailored to fit your data needs, integrations, and workflows.', icon: 'code', color: 'green' },
        { title: 'Consulting', desc: 'Expert consulting for API integrations, helping teams design and optimize custom solutions.', icon: 'lightbulb', color: 'amber' },
        { title: 'Available SLAs', desc: 'Service level agreements ensuring guaranteed uptime, priority support, and performance standards.', icon: 'sparkle', color: 'cyan' },
        { title: 'Integrations', desc: 'Enterprise-ready API integrations to connect real-time social data to your existing applications.', icon: 'globe', color: 'green' },
        { title: 'Team Access', desc: 'Team access to LunarCrush, enabling multiple users to monitor data across departments.', icon: 'users', color: 'purple' },
        { title: 'Priority Support', desc: 'Personalized onboarding and ongoing technical assistance for a seamless experience.', icon: 'shieldCheck', color: 'cyan' },
      ],
      order: 4,
    },
  ],

  navigation: [
    { label: 'Platform', type: 'dropdown_parent', order: 1 },
    { label: 'LunarCrush API', type: 'dropdown_child', parent: 'Platform', order: 2 },
    { label: 'LunarCrush MCP', type: 'dropdown_child', parent: 'Platform', order: 3 },
    { label: 'LunarCrush CLI', type: 'dropdown_child', parent: 'Platform', order: 4 },
    { label: 'LunarCrush + Claude', type: 'dropdown_child', parent: 'Platform', order: 6 },
    { label: 'LunarCrush + ChatGPT', type: 'dropdown_child', parent: 'Platform', order: 7 },
    { label: 'LunarCrush Discover', type: 'dropdown_child', parent: 'Platform', order: 1 },
    { label: 'LunarCrush Collections', type: 'dropdown_child', parent: 'Platform', order: 5 },
    { label: 'Fantasy Football', type: 'dropdown_child', parent: 'Platform', order: 8 },
    { label: 'Pricing', type: 'link', order: 2 },
    { label: 'Enterprise', type: 'link', order: 3 },
    { label: 'Use Cases', type: 'link', order: 4 },
    { label: 'Blog', type: 'link', order: 5 },
    { label: 'Log in', type: 'login', order: 99 },
    { label: 'Sign Up', type: 'cta', order: 100 },
  ],

  footerColumns: [
    {
      title: 'Platform',
      links: [
        'LunarCrush API',
        'LunarCrush MCP',
        'LunarCrush CLI',
        'LunarCrush + Claude',
        'LunarCrush Discover',
        'LunarCrush Collections',
      ],
    },
    {
      title: 'Company',
      links: ['About', 'Careers', 'FAQs', 'Glossary', 'Shop', 'Brand Resources'],
    },
  ],

  footerSocialLinks: ['X', 'Telegram', 'YouTube', 'Instagram'],

  faqs: [],
  blogPosts: [],
};
